import { makeStyles } from '@material-ui/core/styles';
import orderBy from 'lodash/orderBy';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  SearchInput,
  Button,
} from 'src/common/ui/components';
import useTypedSWR from 'src/components/general/useTypedSWR';
import { ApolloClientContext } from 'src/data/ApolloClientContext';
import { RequestRowQueueEntry } from 'src/dropInClinic/RequestRow';
import { ACCEPT_FOR_MUTATION } from 'src/dropInClinic/queries';
import { GET_SCHEDULING_PROVIDERS } from 'src/dropInClinic/queries/getSchedulingProviders.gql';
import { AcceptForMutationArgs, AcceptForMutationResponse } from 'src/dropInClinic/types';
import type {
  GetSchedulingProvidersQuery,
  GetSchedulingProvidersQueryVariables,
  Staff_DropInClinicRequestType as DropInClinicRequestType,
} from 'src/generated/gql/graphql';

interface AcceptForDialogProps {
  open: boolean;
  onClose: () => void;
  entry: RequestRowQueueEntry;
}

export const AcceptForDialog: React.FC<AcceptForDialogProps> = ({ open, onClose, entry }) => {
  const { patient, requestType, requestId } = entry;

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const classes = useStyles();

  const {
    data,
    error: schedulingProviderListError,
    isValidating: loading,
  } = useTypedSWR<GetSchedulingProvidersQuery, GetSchedulingProvidersQueryVariables>([
    GET_SCHEDULING_PROVIDERS,
    { patientId: patient.id, requestType: requestType as DropInClinicRequestType },
  ]);

  useEffect(() => {
    if (schedulingProviderListError) {
      setError(`Failed to load providers: ${schedulingProviderListError}`);
    }
  }, [schedulingProviderListError]);

  const { apolloClient } = useContext(ApolloClientContext);

  // Filter providers by search (case-insensitive, matches first or last name)
  const filteredproviders = useMemo(() => {
    const providers = data?.staff_getSchedulingProviders ?? [];
    const term = search.trim().toLowerCase();
    let filtered = providers;
    if (term) {
      filtered = providers.filter(p =>
        [p.firstName, p.lastName].some(v => v?.toLowerCase().includes(term)),
      );
    }
    return orderBy(filtered, [p => p.firstName, p => p.lastName]);
  }, [data?.staff_getSchedulingProviders, search]);

  const handleSelect = (id: string) => setSelectedId(id);

  const handleAccept = useCallback(async () => {
    if (!apolloClient || !selectedId) {
      setError('No selected providerId or Apollo Client has not loaded.');
      return;
    }

    try {
      const response = await apolloClient.mutate<AcceptForMutationResponse, AcceptForMutationArgs>({
        mutation: ACCEPT_FOR_MUTATION,
        variables: { requestId, providerId: selectedId },
      });

      if (!response.data?.staff_acceptDropInClinicRequestFor) {
        setError('Unable to accept for provider. Does the request have a valid status?');
      } else {
        onClose();
      }
    } catch (e) {
      setError(`Failed accepting for provider: ${e}`);
    }
  }, [apolloClient, selectedId, requestId, onClose]);

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle>
        {patient.firstName} {patient.preferredName && `"${patient.preferredName}"`}{' '}
        {patient.lastName}
      </DialogTitle>
      <DialogContent>
        <SearchInput onChange={setSearch} placeholder="Search People..." color="ice" />
        <div className={classes.providerListContainer}>
          {loading && <div className={classes.emptyState}>Loading providers...</div>}
          {error && <div className={classes.emptyState}>{error}</div>}
          {!loading && !error && filteredproviders.length === 0 && (
            <div className={classes.emptyState}>No providers found</div>
          )}
          {!loading &&
            !error &&
            filteredproviders.map(provider => (
              <div
                key={provider.id}
                onClick={() => handleSelect(provider.id)}
                className={
                  provider.id === selectedId
                    ? `${classes.providerRow} ${classes.selected}`
                    : classes.providerRow
                }
                data-testid={`accept-for-provider-${provider.id}`}
              >
                {provider.firstName} {provider.lastName}
              </div>
            ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleAccept} disabled={!selectedId}>
          ✓ Accept For
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles({
  providerListContainer: {
    marginTop: 16,
  },
  emptyState: {
    color: '#888',
    padding: '24px 0',
    textAlign: 'center',
  },
  providerRow: {
    cursor: 'pointer',
    padding: 12,
    borderBottom: '1px solid #f5f5f5',
    fontSize: 16,
    fontFamily: 'Nunito, Nunito Sans',
    display: 'flex',
    alignItems: 'center',
    background: 'none',
    transition: 'background 0.2s',
    '&:hover': {
      background: '#f5f8fa',
    },
  },
  selected: {
    background: '#f0f4f8',
  },
});

export default AcceptForDialog;
